<template>
  <section :class="['asc-image', `asc-image--${display}`]">
    <div
      :id="`asc-image-editor_${_uid}`"
      :class="{
        'asc-image-editor--focused': selected,
        'asc-image-editor--resizing': resizing,
        'asc-image-editor--editable': view.editable,
      }"
      class="asc-image-editor"
    >
      <v-menu
        v-model="showMenu"
        tile
        :close-on-content-click="false"
        content-class="asc-image-editor--menu"
        :attach="`#asc-image-editor_${_uid}`"
        @input="handleMenu"
      >
        <template #activator="{ on, attrs }">
          <img
            :src="src"
            :title="node.attrs.title"
            :alt="node.attrs.alt"
            :width="width"
            class="asc-image-editor-image"
            v-bind="attrs"
            @click="handleImageSelection"
            v-on="view.editable ? on : false"
          />
        </template>
        <v-toolbar dense tile class="asc-image-editor--toolbar" color="#e8e8e8">
          <v-btn-toggle
            :value="display"
            borderless
            group
            @click.stop
            @change="handleImageDisplayChange"
          >
            <asc-tooltip
              v-for="displayType in getDisplayTypes"
              :key="displayType.value"
              top
              :tooltip="display.text"
            >
              <v-btn
                icon
                tile
                :value="displayType.value"
                :ripple="false"
                @click.stop
              >
                <v-icon :size="16" :color="getIconColor">{{
                  displayType.icon
                }}</v-icon>
              </v-btn>
            </asc-tooltip>
          </v-btn-toggle>
          <v-divider vertical></v-divider>
          <asc-tooltip tooltip="Reset image size" top>
            <v-btn icon tile :ripple="false" @click.stop="handleImageReset">
              <v-icon :size="16" :color="getIconColor">mdi-aspect-ratio</v-icon>
            </v-btn>
          </asc-tooltip>
          <v-divider vertical></v-divider>
          <asc-tooltip tooltip="Open image in new tab" top>
            <v-btn icon tile :ripple="false" @click.stop="handleImageOpen">
              <v-icon :size="16" :color="getIconColor">mdi-open-in-new</v-icon>
            </v-btn>
          </asc-tooltip>
          <v-divider vertical></v-divider>
          <asc-tooltip tooltip="Remove image" top>
            <v-btn icon tile :ripple="false" @click.stop="handleImageRemove">
              <v-icon :size="16" :color="getIconColor">mdi-image-remove</v-icon>
            </v-btn>
          </asc-tooltip>
        </v-toolbar>
      </v-menu>

      <div
        v-if="view.editable"
        v-show="selected || resizing"
        :class="[
          'asc-image-resizer',
          resizing ? 'asc-image-resizer--active' : '',
        ]"
      >
        <span
          v-for="direction in resizeDirections"
          :key="direction"
          :class="[
            resizing ? 'asc-image-resizer--active--handler' : '',
            'asc-image-resizer--handler',
            `asc-image-resizer--handler--${direction}`,
          ]"
          @mousedown="onMouseDown($event, direction)"
        />
      </div>
    </div>
  </section>
</template>

<script>
import { NodeSelection } from 'prosemirror-state'
import { ResizeObserver } from '@juggle/resize-observer'
import { deleteSelection } from 'prosemirror-commands'

import AscTooltip from '@/components/atoms/AscTooltip'

import variables from '@/styles/_varsJs.scss'
import { ImageDisplay } from '@/utilities/image'

const IMAGE_CACHE = {}
export function resolveImg(src) {
  return new Promise((resolve, reject) => {
    const result = {
      complete: false,
      width: 0,
      src,
    }

    if (!src) {
      reject(result)
      return
    }

    if (IMAGE_CACHE[src]) {
      resolve({ ...IMAGE_CACHE[src] })
      return
    }

    const img = new Image()

    img.onload = () => {
      result.width = img.width
      result.complete = true

      IMAGE_CACHE[src] = { ...result }
      resolve(result)
    }

    img.onerror = () => {
      reject(result)
    }

    img.src = src
  })
}

const ResizeDirection = {
  TOP_LEFT: 'tl',
  TOP_RIGHT: 'tr',
  BOTTOM_LEFT: 'bl',
  BOTTOM_RIGHT: 'br',
}

const MIN_SIZE = 200
const MAX_SIZE = 1000

export default {
  name: 'AscImageEditor',

  components: {
    AscTooltip,
  },

  props: {
    node: {
      type: Object,
      required: true,
    },
    view: {
      type: Object,
      required: true,
    },
    getPos: {
      type: Function,
      required: true,
    },
    updateAttrs: {
      type: Function,
      required: true,
    },
    selected: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      dp: 'inline',
      imageSliderStep: 1,
      showMenu: false,
      x: 0,
      y: 0,
      maxSize: {
        width: MAX_SIZE,
      },
      originalSize: {
        width: 0,
      },
      resizeOb: new ResizeObserver(() => {
        this.getMaxSize()
      }),
      resizeDirections: [
        ResizeDirection.TOP_LEFT,
        ResizeDirection.TOP_RIGHT,
        ResizeDirection.BOTTOM_LEFT,
        ResizeDirection.BOTTOM_RIGHT,
      ],
      imageDisplay: [
        {
          value: ImageDisplay.INLINE,
          icon: 'mdi-format-wrap-inline',
          text: 'Inline display',
          visible: true,
        },
        {
          value: ImageDisplay.BREAK_TEXT,
          icon: 'mdi-format-wrap-top-bottom',
          text: 'Full width display',
          visible: true,
        },
        // Issue details dynamic tiptap menu show/hide has some issue with floating images
        {
          value: ImageDisplay.FLOAT_LEFT,
          icon: 'mdi-format-float-left',
          text: 'Move to left',
          visible: false,
        },
        {
          value: ImageDisplay.FLOAT_RIGHT,
          icon: 'mdi-format-float-right',
          text: 'Move to right',
          visible: false,
        },
      ],
      resizing: false,
      resizerState: {
        x: 0,
        y: 0,
        w: 0,
        h: 0,
        dir: '',
      },
      imageAttrs: {},
    }
  },

  computed: {
    getIconColor() {
      return variables.primaryblack
    },

    src() {
      return this.node.attrs.src
    },

    width() {
      return this.node.attrs.width
    },

    display() {
      return this.node.attrs.display
    },

    getDisplayTypes() {
      return this.imageDisplay.filter(function (display) {
        return display.visible
      })
    },

    getImageAttrs() {
      return {
        src: this.node.attrs.src,
        alt: this.node.attrs.alt,
        width: this.node.attrs.width,
        display: this.node.attrs.display,
      }
    },
  },
  watch: {
    display(display) {
      this.dp = display
    },
  },
  async created() {
    const result = await resolveImg(this.src)

    if (!result.complete) {
      result.width = MIN_SIZE
    }

    this.originalSize = {
      width: result.width,
    }
  },

  mounted() {
    this.resizeOb.observe(this.view.dom)
  },

  beforeDestroy() {
    this.resizeOb.disconnect()
  },

  methods: {
    getMaxSize() {
      const { width } = getComputedStyle(this.view.dom)
      this.maxSize.width = parseInt(width, 10)
    },

    calculateWidth(val, min, max) {
      if (val < min) return min
      if (val > max) return max
      return val
    },

    onMouseDown(e, dir) {
      e.preventDefault()
      e.stopPropagation()

      this.resizerState.x = e.clientX
      this.resizerState.y = e.clientY

      const originalWidth = this.originalSize.width
      const originalHeight = this.originalSize.height
      const aspectRatio = originalWidth / originalHeight

      let { width, height } = this.node.attrs
      const maxWidth = this.maxSize.width

      if (width && !height) {
        width = width > maxWidth ? maxWidth : width
        height = Math.round(width / aspectRatio)
      } else if (height && !width) {
        width = Math.round(height * aspectRatio)
        width = width > maxWidth ? maxWidth : width
      } else if (!width && !height) {
        width = originalWidth > maxWidth ? maxWidth : originalWidth
        height = Math.round(width / aspectRatio)
      } else {
        width = width > maxWidth ? maxWidth : width
      }

      this.resizerState.w = width
      this.resizerState.h = height
      this.resizerState.dir = dir

      this.resizing = true
      // Hide menu on resize start
      this.showMenu = false

      this.onEvents()
    },

    onMouseMove(e) {
      e.preventDefault()
      e.stopPropagation()
      if (!this.resizing) return

      const { x, w, dir } = this.resizerState

      const dx = (e.clientX - x) * (/l/.test(dir) ? -1 : 1)

      this.updateAttrs({
        width: this.calculateWidth(w + dx, MIN_SIZE, this.maxSize.width),
      })
    },

    onMouseUp(e) {
      e.preventDefault()
      e.stopPropagation()
      if (!this.resizing) return

      this.resizing = false

      this.resizerState = {
        x: 0,
        y: 0,
        w: 0,
        h: 0,
        dir: '',
      }

      this.offEvents()
      this.handleImageSelection()
    },

    onEvents() {
      document.addEventListener('mousemove', this.onMouseMove, true)
      document.addEventListener('mouseup', this.onMouseUp, true)
    },

    offEvents() {
      document.removeEventListener('mousemove', this.onMouseMove, true)
      document.removeEventListener('mouseup', this.onMouseUp, true)
    },

    handleMenu(visible) {
      if (visible) {
        // Reset menu
        this.imageAttrs = this.getImageAttrs
      }
    },

    handleImageSelection() {
      const { state } = this.view
      let { tr } = state
      const selection = NodeSelection.create(state.doc, this.getPos())
      tr = tr.setSelection(selection)
      this.view.dispatch(tr)
    },

    handleImageDisplayChange(display) {
      if (display) {
        this.updateAttrs({ display: display })
        this.showMenu = false
      }
    },

    handleImageOpen() {
      if (this.src) {
        const w = window.open('about:blank')
        setTimeout(() => {
          w.document.body.style.margin = 0
          w.document.body.appendChild(w.document.createElement('iframe'))
          const iframeEle = w.document.getElementsByTagName('iframe')[0]
          const iframeDoc =
            iframeEle.contentDocument || iframeEle.contentWindow.document

          iframeDoc.open()
          iframeDoc.write(
            `<html><body style="margin:0"><img alt="" style="max-width:100%" src="${this.src}"></body></html>`,
          )
          iframeDoc.close()

          iframeEle.style.width = '100%'
          iframeEle.style.height = '100%'
          iframeEle.style.border = 0
        }, 0)
        this.showMenu = false
      }
    },

    handleImageReset() {
      this.updateAttrs({
        alt: this.imageAttrs.alt,
        width: this.originalSize.width,
      })
      this.showMenu = false
    },

    handleImageRemove() {
      const { state, dispatch } = this.view
      deleteSelection(state, dispatch)
    },
  },
}
</script>

<style lang="scss">
$img-space: 0;
$floating-img-space: 10px;
$resize-handler-dimensions: 6px;
$resize-handler-position: -$resize-handler-dimensions/2;

.asc-image {
  display: inline-block;
  float: none;
  line-height: 0;
  margin: $img-space 0;
  max-width: 100%;
  user-select: none;
  vertical-align: baseline;
  white-space: normal;

  &--inline {
    margin-left: $img-space;
    margin-right: $floating-img-space;
  }

  &--block {
    display: block;
  }

  &--left {
    float: left;
    margin-left: 0;
    margin-right: $floating-img-space;
  }

  &--right {
    float: right;
    margin-left: $floating-img-space;
    margin-right: 0;
  }

  &-editor {
    clear: both;
    display: inline-block;
    max-width: 100%;
    outline-color: transparent;
    outline-style: solid;
    outline-width: 1px;
    transition: all 0.2s ease-in;
    position: relative;

    &-image {
      cursor: inherit;
      margin: 0;
    }

    &.asc-image-editor--editable {
      &:hover {
        outline-color: $accent-300;
      }

      .asc-image-editor-image {
        cursor: pointer;
      }
    }

    &--focused:hover,
    &--resizing:hover {
      outline-color: transparent;
    }

    &--menu {
      min-width: auto !important;
    }

    &--toolbar {
      .v-toolbar__content {
        padding: 0;
      }
    }
  }

  &-resizer {
    border: 1px solid $accent-600;
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 100%;
    z-index: 1;

    &--handler {
      background-color: $accent-600;
      box-shadow: 0 1px 1px 0 rgba(0, 0, 0, 0.5);
      box-sizing: border-box;
      display: block;
      position: absolute;
      height: $resize-handler-dimensions;
      width: $resize-handler-dimensions;
      z-index: 2;

      &--tl {
        left: $resize-handler-position;
        top: $resize-handler-position;
        cursor: nw-resize;
      }

      &--tr {
        right: $resize-handler-position;
        top: $resize-handler-position;
        cursor: ne-resize;
      }

      &--bl {
        bottom: $resize-handler-position;
        left: $resize-handler-position;
        cursor: sw-resize;
      }

      &--br {
        bottom: $resize-handler-position;
        right: $resize-handler-position;
        cursor: se-resize;
      }
    }
  }

  &-resizer--active {
    border: 1px solid $accent-700;

    &--handler {
      background-color: $accent-700;
    }
  }
}
</style>
